import { useLayoutEffect, useRef, ReactNode } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface SplitCharsProps {
  text: string;
  className?: string;
  /** Character entrance is also driven by initial reveal, then ScrollTrigger scrubs. */
  delay?: number;
  /** Optional render override per character (e.g. for gradient styling) */
  charClassName?: string;
  as?: "span" | "div";
  children?: ReactNode;
}

/**
 * Splits a string into per-character spans and animates them upward
 * from opacity 0 → 1, scrubbed by ScrollTrigger as the user scrolls.
 * Respects prefers-reduced-motion.
 */
export default function SplitChars({
  text,
  className = "",
  delay = 0,
  charClassName = "",
  as: Tag = "span",
}: SplitCharsProps) {
  const containerRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(el.querySelectorAll(".sc-char"), { opacity: 1, yPercent: 0 });
      return;
    }

    const chars = el.querySelectorAll<HTMLElement>(".sc-char");

    const ctx = gsap.context(() => {
      // Initial entrance
      gsap.fromTo(
        chars,
        { opacity: 0, yPercent: 110, rotateX: -45 },
        {
          opacity: 1,
          yPercent: 0,
          rotateX: 0,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.025,
          delay,
        }
      );

      // Scroll-scrubbed re-reveal so users feel the chars rise back as they scroll up
      gsap.fromTo(
        chars,
        { yPercent: 0, opacity: 1 },
        {
          yPercent: -40,
          opacity: 0.15,
          ease: "none",
          stagger: 0.01,
          scrollTrigger: {
            trigger: el,
            start: "top top+=80",
            end: "bottom top+=80",
            scrub: true,
          },
        }
      );
    }, el);

    return () => ctx.revert();
  }, [text, delay]);

  // Split preserving spaces
  const tokens = text.split(/(\s+)/);

  return (
    <Tag
      ref={containerRef as any}
      className={`inline-block [perspective:1000px] ${className}`}
      aria-label={text}
    >
      {tokens.map((token, ti) => {
        if (/^\s+$/.test(token)) {
          return <span key={`s-${ti}`}>&nbsp;</span>;
        }
        return (
          <span key={`w-${ti}`} className="inline-block whitespace-nowrap">
            {Array.from(token).map((ch, ci) => (
              <span
                key={`c-${ti}-${ci}`}
                aria-hidden="true"
                className={`sc-char inline-block will-change-transform ${charClassName}`}
                style={{ transformOrigin: "50% 100%" }}
              >
                {ch}
              </span>
            ))}
          </span>
        );
      })}
    </Tag>
  );
}
